const Firehose = require('./firehose')

/**
 * The credentials from AWS
 * @typedef {Object} CognitoCredentials
 * @property {string} AccessKeyId - The access key id
 * @property {string} SecretKey - The secret key
 * @property {string} SessionToken - The session token
 * @property {number} Expiration - The expiration in seconds
 */

/**
 * How often to check whether there are expired cache
 * items.
 */
const EXPIRY_CHECK_MILLISECONDS = 60000

class CredentialsCache {
  constructor() {
    /**
     * Credentials cache
     * @type {Record<string, { expires: Date, value: CognitoCredentials }>}
     * @public
     */
    this.cache = {}

    this.checkExpiredItems()
  }

  /**
   * Check at intervals whether to remove expired
   * items from the cache.
   */
  checkExpiredItems() {
    setInterval(() => {
      this.removeExpiredItems()
    }, EXPIRY_CHECK_MILLISECONDS)
  }

  removeExpiredItems() {
    Object.keys(this.cache).forEach((cacheKey) => {
      if (cacheKey in this.cache && Date.now() > this.cache[cacheKey].expires) {
        delete this.cache[cacheKey]
      }
    })
  }

  get(key) {
    if (key in this.cache && this.cache[key] != null) {
      return this.cache[key].value
    }
    return null
  }

  /**
   *
   * @param {string} key - The cache key
   * @param {CognitoCredentials} value - The credentials
   * @param {number} expiryMilliseconds - The cache expiry in milliseconds
   * @returns
   */
  set(key, value, expiryMilliseconds) {
    this.cache[key] = { expires: expiryMilliseconds, value }
    return this.cache
  }
}

class CredentialsManager {
  constructor() {
    this.credentialsCache = new CredentialsCache()

    /**
     * Credentials promise store contains in-flight
     * requests with the key as the identity id.
     * @type {Record<string, Promise<CognitoCredentials>>}
     * @public
     */
    this.credentialsPromiseStore = {}
  }

  /**
   * Retrieve firehose credentials using the identity id, this
   * will store in-flight requests by the id so any requests made
   * whilst the request is in-flight will use the same request. If
   * a value already exists in the cache, it will use this value.
   *
   * @param {string} identityId - The identity id
   * @param {bool} bypassCache - Bypass the cache and request new credentials
   * @returns {Promise<CognitoCredentials>}
   */
  getCredentialsAsync(identityId, bypassCache) {
    if (this.credentialsPromiseStore[identityId] != null) {
      return this.credentialsPromiseStore[identityId]
    }

    if (bypassCache !== true && this.credentialsCache.get(identityId) != null) {
      return Promise.resolve(this.credentialsCache.get(identityId))
    }

    const credentialsPromise = (async () => {
      try {
        const credentials = await Firehose.getCredentials(identityId)

        if (credentials != null && credentials.Expiration != null) {
          this.credentialsCache.set(
            identityId,
            credentials,
            /**
             * Expiration comes back in seconds, here we convert to milliseconds,
             * we then remove a second to account for cache invalidation timing.
             */
            credentials.Expiration * 1000 - EXPIRY_CHECK_MILLISECONDS
          )
        }

        return credentials
      } finally {
        delete this.credentialsPromiseStore[identityId]
      }
    })()

    this.credentialsPromiseStore[identityId] = credentialsPromise

    return credentialsPromise
  }
}

module.exports = CredentialsManager
